import { AuthUser, getAuthUserState, GyymApiClient } from '@gyym/sdk'
import type { GetServerSideProps, GetServerSidePropsContext } from 'next'

import { createServerGyymApiClient } from '#utils/createServerGyymApiClient'
import { gyymPrefetch } from '#utils/gyymPrefetch'
import { ServerCachedContext, SSRPrefetch } from '#utils/types'

export const withGyymSSR = <P extends { initialUser?: AuthUser }>(
  prefetch: SSRPrefetch,
): GetServerSideProps => {
  return async (
    ctx: GetServerSidePropsContext,
    { client: cachedClient, composedProps }: ServerCachedContext<P> = {},
  ) => {
    const client: GyymApiClient =
      cachedClient ?? createServerGyymApiClient({ ctx })

    try {
      const [, authUser] = composedProps?.initialUser
        ? ['', composedProps?.initialUser]
        : await gyymPrefetch(client.auth.user.get)

      const authState = getAuthUserState(authUser)

      const ret = await prefetch(ctx, client, {
        authUser,
        authState,
      })

      if (!ret) {
        throw new Error('Invalid return value from prefetch function')
      }

      if (!Array.isArray(ret) && ret.notFound) {
        return {
          notFound: true,
        } as const
      }

      const { cache, props } = Array.isArray(ret)
        ? { cache: ret, props: {} }
        : ret

      const results = await Promise.all(
        cache.map(async (config) => {
          // Skip request if apiRequest is not returned from prefetchConfig function
          if (!config) return Promise.resolve({})

          const [key, result] = await Promise.resolve(config)

          // Don't cache empty results
          if (result === null || result === undefined) {
            return Promise.resolve({})
          }

          return {
            [key]: result,
          }
        }),
      )

      const fallback = results.reduce(
        (acc, result) => ({
          ...acc,
          ...result,
        }),
        {},
      )

      return {
        props: {
          ...props,
          swr: {
            fallback,
          },
        },
      }
    } catch (e) {
      if (client.isHttpError(e) && e.status === 404) {
        return {
          notFound: true,
        } as const
      }

      console.error(e)

      throw e
    }
  }
}
